import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';

const AssignmentHistory = ({ onClose, isDarkMode, userId }) => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  // userId -> index dne který se zobrazuje
  const [expandedDays, setExpandedDays] = useState({});
  const [dailyData, setDailyData] = useState(null);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState('');
  // klíč "userId|batchName" -> pole firem (IČO, název, stav, forecast)
  const [companyDetails, setCompanyDetails] = useState({});
  const [loadingDetailKey, setLoadingDetailKey] = useState(null);
  // klíč "userId|batchName" -> { status, forecast, search }
  const [detailFilters, setDetailFilters] = useState({});

  useEffect(() => {
    fetchAssignmentStats();
    setExpandedDays({});
    setDailyData(null);
    setSelectedBatch('');
    setCompanyDetails({});
    setDetailFilters({});
  }, [selectedMonth, selectedYear]);

  const fetchAssignmentStats = async () => {
    try {
      setLoading(true);
      const uid = userId || localStorage.getItem('lastUserId') || '';
      const response = await fetch(
        `${API_URL}/assignment-history?month=${selectedMonth}&year=${selectedYear}&userId=${uid}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Chyba při načítání historie přidělování:', error);
      setStats([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyData = async () => {
    if (dailyData) return dailyData;
    try {
      setLoadingDaily(true);
      const uid = userId || localStorage.getItem('lastUserId') || '';
      const response = await fetch(
        `${API_URL}/assignment-history-daily?month=${selectedMonth}&year=${selectedYear}&userId=${uid}`
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setDailyData(data);
      return data;
    } catch (error) {
      console.error('Chyba při načítání denní historie přidělování:', error);
      return [];
    } finally {
      setLoadingDaily(false);
    }
  };

  const toggleDaysForUser = async (userId) => {
    if (userId in expandedDays) {
      // Skrýt
      const next = { ...expandedDays };
      delete next[userId];
      setExpandedDays(next);
    } else {
      const data = await fetchDailyData();
      const userEntry = data.find(u => u.userId === userId);
      const days = userEntry ? userEntry.days : [];
      // Otevre se na posledním dni (nejnovější)
      setExpandedDays(prev => ({ ...prev, [userId]: days.length - 1 }));
    }
  };

  const getDaysForUser = (userId) => {
    if (!dailyData) return [];
    const userEntry = dailyData.find(u => u.userId === userId);
    return userEntry ? userEntry.days : [];
  };

  const navigateDay = (userId, direction) => {
    const days = getDaysForUser(userId);
    if (days.length === 0) return;
    setExpandedDays(prev => ({
      ...prev,
      [userId]: Math.max(0, Math.min(days.length - 1, (prev[userId] ?? 0) + direction))
    }));
  };

  const formatDayLabel = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('cs-CZ', { weekday: 'long', day: 'numeric', month: 'numeric', year: 'numeric' });
  };

  const toggleCompanyDetails = async (uId, batchName) => {
    const key = `${uId}|${batchName || ''}`;
    if (key in companyDetails) {
      const next = { ...companyDetails };
      delete next[key];
      setCompanyDetails(next);
      return;
    }
    try {
      setLoadingDetailKey(key);
      const uid = userId || localStorage.getItem('lastUserId') || '';
      const params = new URLSearchParams({
        month: String(selectedMonth),
        year: String(selectedYear),
        userId: uid,
        targetUserId: uId,
      });
      if (batchName) params.set('batchName', batchName);
      const response = await fetch(`${API_URL}/assignment-history-companies?${params.toString()}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setCompanyDetails(prev => ({ ...prev, [key]: data }));
    } catch (error) {
      console.error('Chyba při načítání detailu firem:', error);
      setCompanyDetails(prev => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingDetailKey(null);
    }
  };

  const getMonthName = (month) => {
    const months = [
      'Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen',
      'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'
    ];
    return months[month - 1];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'} rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between`}>
          <h2 className="text-2xl font-bold">📥 Historie Přidělování Kontaktů</h2>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
          >
            ✕ Zavřít
          </button>
        </div>

        {/* Filters */}
        <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex gap-4 items-center">
            <label className="font-semibold">Filtr:</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className={`px-4 py-2 rounded border ${
                isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                <option key={month} value={month}>
                  {getMonthName(month)}
                </option>
              ))}
            </select>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className={`px-4 py-2 rounded border ${
                isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <button
              onClick={fetchAssignmentStats}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold disabled:bg-gray-400"
            >
              {loading ? 'Načítání...' : 'Obnovit'}
            </button>
            {/* Filtr podle šarže */}
            {stats.length > 0 && (() => {
              const allBatches = [...new Set(
                stats.flatMap(u => u.batches.map(b => b.batchName).filter(Boolean))
              )].sort();
              return allBatches.length > 0 ? (
                <select
                  value={selectedBatch}
                  onChange={(e) => setSelectedBatch(e.target.value)}
                  className={`px-4 py-2 rounded border ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="">Všechny šarže</option>
                  {allBatches.map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              ) : null;
            })()}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Načítání...</div>
          ) : stats.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Žádné záznamy o přidělování pro vybraný měsíc
            </div>
          ) : (
            <div className="space-y-6">
              {stats.map((userStat) => {
                // Filtruj pouze šarže s názvem a spočítej celkový počet
                const batchesWithName = userStat.batches
                  .filter(b => b.batchName)
                  .filter(b => !selectedBatch || b.batchName === selectedBatch);
                const totalCount = batchesWithName.reduce((sum, b) => sum + b.count, 0);
                
                // Pokud uživatel nemá žádné šarže s názvem, přeskoč ho
                if (batchesWithName.length === 0) return null;
                
                return (
                <div key={userStat.userId} className={`rounded-lg p-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold">{userStat.userName}</h3>
                    <div className="flex gap-4 items-center">
                      <button
                        onClick={() => toggleDaysForUser(userStat.userId)}
                        disabled={loadingDaily}
                        className={`text-sm px-3 py-1 rounded-lg border font-medium transition-colors ${
                          userStat.userId in expandedDays
                            ? isDarkMode ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-indigo-100 border-indigo-400 text-indigo-700'
                            : isDarkMode ? 'bg-gray-600 border-gray-500 text-gray-200 hover:bg-gray-500' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {loadingDaily && !dailyData ? '⏳ Načítám...' : userStat.userId in expandedDays ? '📅 Skrýt dny' : '📅 Zobrazit dny'}
                      </button>
                      <div className="text-right">
                        <div className="text-sm text-gray-500 dark:text-gray-400">Celkem kontaktů</div>
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                          {totalCount}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Šarže */}
                  {batchesWithName.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className={`${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                            <th className="px-4 py-2 text-left font-semibold">Šarže</th>
                            <th className="px-4 py-2 text-center font-semibold">Počet</th>
                            <th className="px-4 py-2 text-left font-semibold">Datum prvního</th>
                            <th className="px-4 py-2 text-left font-semibold">Datum posledního</th>
                          </tr>
                        </thead>
                        <tbody>
                          {batchesWithName.map((batch, index) => {
                            const detailKey = `${userStat.userId}|${batch.batchName || ''}`;
                            const isDetailOpen = detailKey in companyDetails;
                            const detailRows = companyDetails[detailKey] || [];
                            return (
                            <React.Fragment key={index}>
                            <tr
                              className={`border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}
                            >
                              <td className="px-4 py-2 font-medium">{batch.batchName}</td>
                              <td className="px-4 py-2 text-center">
                                <button
                                  onClick={() => toggleCompanyDetails(userStat.userId, batch.batchName)}
                                  disabled={loadingDetailKey === detailKey}
                                  title="Klikněte pro zobrazení firem (IČO, název, stav, FC)"
                                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full font-semibold transition-colors ${
                                    isDetailOpen
                                      ? 'bg-indigo-600 text-white'
                                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                                  } disabled:opacity-60`}
                                >
                                  {loadingDetailKey === detailKey ? '⏳' : (isDetailOpen ? '▾' : '▸')} {batch.count}
                                </button>
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                                {new Date(batch.firstAssignment).toLocaleString('cs-CZ')}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                                {new Date(batch.lastAssignment).toLocaleString('cs-CZ')}
                              </td>
                            </tr>
                            {isDetailOpen && (
                              <tr className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                                <td colSpan={4} className="px-4 py-3">
                                  {detailRows.length === 0 ? (
                                    <div className="text-center py-2 text-sm text-gray-400">Žádné firmy</div>
                                  ) : (() => {
                                    const filters = detailFilters[detailKey] || { status: '', forecast: '', search: '' };
                                    const setFilter = (patch) => setDetailFilters(prev => ({
                                      ...prev,
                                      [detailKey]: { ...(prev[detailKey] || { status: '', forecast: '', search: '' }), ...patch }
                                    }));
                                    const fcLabel = (v) => (v === '' || v === null || v === undefined) ? 'Bez FC' : String(v);
                                    // Počty podle forecastu (ze všech firem, bez ohledu na filtr)
                                    const fcCounts = {};
                                    detailRows.forEach(c => {
                                      const key = (c.forecast === '' || c.forecast === null || c.forecast === undefined) ? '' : String(c.forecast);
                                      fcCounts[key] = (fcCounts[key] || 0) + 1;
                                    });
                                    const fcKeys = Object.keys(fcCounts).sort((a, b) => {
                                      if (a === '') return 1;
                                      if (b === '') return -1;
                                      const na = parseFloat(a), nb = parseFloat(b);
                                      if (!isNaN(na) && !isNaN(nb)) return nb - na;
                                      return a.localeCompare(b, 'cs');
                                    });
                                    const statusOptions = [...new Set(detailRows.map(c => c.status).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'cs'));
                                    const searchLower = (filters.search || '').trim().toLowerCase();
                                    const filteredRows = detailRows.filter(c => {
                                      if (filters.status && c.status !== filters.status) return false;
                                      const fcKey = (c.forecast === '' || c.forecast === null || c.forecast === undefined) ? '' : String(c.forecast);
                                      if (filters.forecast !== '' && filters.forecast !== undefined && filters.forecast !== null) {
                                        if (filters.forecast === '__none__') {
                                          if (fcKey !== '') return false;
                                        } else if (fcKey !== filters.forecast) {
                                          return false;
                                        }
                                      }
                                      if (searchLower) {
                                        const hay = `${c.companyName || ''} ${c.companyIco || ''}`.toLowerCase();
                                        if (!hay.includes(searchLower)) return false;
                                      }
                                      return true;
                                    });
                                    return (
                                    <div>
                                      {/* Počty zákazníků podle FC */}
                                      <div className="flex flex-wrap gap-2 mb-3">
                                        {fcKeys.map(k => {
                                          const active = filters.forecast === (k === '' ? '__none__' : k);
                                          return (
                                            <button
                                              key={k || '__none__'}
                                              onClick={() => setFilter({ forecast: active ? '' : (k === '' ? '__none__' : k) })}
                                              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                                                active
                                                  ? 'bg-indigo-600 border-indigo-500 text-white'
                                                  : isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                                              }`}
                                              title="Klikněte pro filtrování dle FC"
                                            >
                                              FC {fcLabel(k)}: {fcCounts[k]}
                                            </button>
                                          );
                                        })}
                                      </div>

                                      {/* Filtry */}
                                      <div className="flex flex-wrap gap-2 items-center mb-3">
                                        <input
                                          type="text"
                                          value={filters.search}
                                          onChange={(e) => setFilter({ search: e.target.value })}
                                          placeholder="Hledat název / IČO…"
                                          className={`px-3 py-1.5 text-sm rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300'}`}
                                        />
                                        <select
                                          value={filters.status}
                                          onChange={(e) => setFilter({ status: e.target.value })}
                                          className={`px-3 py-1.5 text-sm rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                                        >
                                          <option value="">Všechny stavy</option>
                                          {statusOptions.map(s => (
                                            <option key={s} value={s}>{s}</option>
                                          ))}
                                        </select>
                                        <select
                                          value={filters.forecast}
                                          onChange={(e) => setFilter({ forecast: e.target.value })}
                                          className={`px-3 py-1.5 text-sm rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                                        >
                                          <option value="">Všechny FC</option>
                                          {fcKeys.map(k => (
                                            <option key={k || '__none__'} value={k === '' ? '__none__' : k}>{fcLabel(k)}</option>
                                          ))}
                                        </select>
                                        {(filters.status || filters.forecast || filters.search) && (
                                          <button
                                            onClick={() => setFilter({ status: '', forecast: '', search: '' })}
                                            className={`px-3 py-1.5 text-sm rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-100'}`}
                                          >
                                            ✕ Zrušit filtry
                                          </button>
                                        )}
                                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
                                          Zobrazeno {filteredRows.length} z {detailRows.length}
                                        </span>
                                      </div>

                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                                            <th className="px-3 py-2 text-left font-semibold">IČO</th>
                                            <th className="px-3 py-2 text-left font-semibold">Název zákazníka</th>
                                            <th className="px-3 py-2 text-left font-semibold">Stav</th>
                                            <th className="px-3 py-2 text-left font-semibold">FC</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {filteredRows.length === 0 ? (
                                            <tr>
                                              <td colSpan={4} className="px-3 py-3 text-center text-gray-400">Žádné firmy neodpovídají filtru</td>
                                            </tr>
                                          ) : filteredRows.map((c, ci) => (
                                            <tr key={c.companyId || ci} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                                              <td className="px-3 py-1.5 text-gray-600 dark:text-gray-400">{c.companyIco || '—'}</td>
                                              <td className="px-3 py-1.5 font-medium">{c.companyName || '—'}</td>
                                              <td className="px-3 py-1.5">{c.status || '—'}</td>
                                              <td className="px-3 py-1.5">{fcLabel(c.forecast === '' || c.forecast === null || c.forecast === undefined ? '' : String(c.forecast))}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                    );
                                  })()}
                                </td>
                              </tr>
                            )}
                            </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      Žádné šarže s názvem
                    </div>
                  )}

                  {/* Denní rozpad se šipkami */}
                  {userStat.userId in expandedDays && (() => {
                    const days = getDaysForUser(userStat.userId);
                    const idx = expandedDays[userStat.userId] ?? 0;
                    const day = days[idx];
                    return (
                      <div className={`mt-4 rounded-lg p-3 ${isDarkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
                        {/* Navigace šipkami */}
                        <div className="flex items-center justify-between mb-3">
                          <button
                            onClick={() => navigateDay(userStat.userId, -1)}
                            disabled={idx === 0}
                            className={`px-3 py-1 rounded-lg font-bold text-lg transition-colors ${
                              idx === 0
                                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                : isDarkMode ? 'text-white hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            ←
                          </button>
                          <div className="text-center">
                            <div className="font-semibold text-sm">
                              {day ? formatDayLabel(day.date) : '—'}
                            </div>
                            <div className="text-xs text-gray-400">
                              den {idx + 1} z {days.length}
                            </div>
                          </div>
                          <button
                            onClick={() => navigateDay(userStat.userId, 1)}
                            disabled={idx === days.length - 1}
                            className={`px-3 py-1 rounded-lg font-bold text-lg transition-colors ${
                              idx === days.length - 1
                                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                : isDarkMode ? 'text-white hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            →
                          </button>
                        </div>

                        {/* Šarže pro vybraný den */}
                        {!day ? (
                          <div className="text-center py-3 text-gray-400 text-sm">Žádná data</div>
                        ) : (
                          <table className="w-full text-sm">
                            <thead>
                              <tr className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                                <th className="px-4 py-2 text-left font-semibold">Šarže</th>
                                <th className="px-4 py-2 text-center font-semibold">Počet</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(day.batches || []).filter(b => !selectedBatch || b.batchName === selectedBatch).map((b, i) => (
                                <tr key={i} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                                  <td className="px-4 py-2 font-medium">{b.batchName}</td>
                                  <td className="px-4 py-2 text-center">
                                    <span className="inline-block bg-indigo-600 text-white px-3 py-0.5 rounded-full font-semibold">
                                      {b.count}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                              <tr className={`font-bold ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                                <td className="px-4 py-2">Celkem</td>
                                <td className="px-4 py-2 text-center">
                                  <span className="inline-block bg-blue-600 text-white px-3 py-0.5 rounded-full font-semibold">
                                    {(day.batches || []).filter(b => !selectedBatch || b.batchName === selectedBatch).reduce((s, b) => s + b.count, 0)}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        )}
                      </div>
                    );
                  })()}
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignmentHistory;
